import { useState } from "react";

import { useSaveResourceAction } from "~/ai-chat/feature/use-save-resource-action";
import type { SaveResourceAction as SaveResourceActionModel } from "~/ai-chat/model/save-resource-action";
import { ChatSaveResourceCard, type SaveResourceCardState } from "~/ai-chat/ui/chat-save-resource-card";
import { ChatSaveResourceConfirmDialog } from "~/ai-chat/ui/chat-save-resource-confirm-dialog";
import { useGetPlatformInfosecSettings } from "~/api/useInfosecLevels";
import { useAllowedInfosecLevels } from "~/hooks";
import { buildResourcePath } from "~/routes";

interface ChatSaveResourceActionProps {
  action: SaveResourceActionModel;
}

interface PendingSave {
  action: SaveResourceActionModel;
  infosecLevelId?: string;
}

export function ChatSaveResourceAction({ action }: ChatSaveResourceActionProps) {
  const [cardState, setCardState] = useState<SaveResourceCardState>({ status: "ready" });
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);
  const { save } = useSaveResourceAction();
  const platformSettingsQuery = useGetPlatformInfosecSettings();
  const infosecLevels = useAllowedInfosecLevels();

  const handleConfirm = async () => {
    if (pendingSave === null) return;
    const { action: edited, infosecLevelId } = pendingSave;
    setPendingSave(null);
    setCardState({ status: "saving" });
    try {
      const saved = await save(edited, infosecLevelId);
      setCardState({
        status: "saved",
        link: { type: "model", path: buildResourcePath({ resourceId: saved.resourceId }) },
        warning: saved.infosecApplied
          ? undefined
          : "The infosec level could not be applied. Set it from the resource page.",
      });
    } catch {
      setCardState({ status: "error", message: "Could not save the resource. Please try again." });
    }
  };

  return (
    <>
      <ChatSaveResourceCard
        action={action}
        state={cardState}
        infosecEnabled={platformSettingsQuery.data?.enabled}
        infosecLevels={infosecLevels}
        onSave={(edited, infosecLevelId) => setPendingSave({ action: edited, infosecLevelId })}
      />
      <ChatSaveResourceConfirmDialog
        fileName={pendingSave?.action.fileName ?? ""}
        open={pendingSave !== null}
        onConfirm={handleConfirm}
        onCancel={() => setPendingSave(null)}
      />
    </>
  );
}
